import { ServiceType, WorkStatus, WorkType } from "../types/works";
import { Service } from "../types/service";

export const WORK_TYPES: WorkType[] = ["cyclic", "object", "pickup_delivery"];

export const WORK_STATUSES: WorkStatus[] = [
  "draft",
  "planned",
  "in_progress",
  "paused",
  "completed",
  "overdue",
  "cancelled",
];

/**
 * Fixed works service catalog — these are the on-site work service types
 * (matching the mockup), NOT the company's Services billing directory.
 * `id` is the stable code persisted as a service line's serviceTypeId.
 */
export const WORK_SERVICE_TYPES: ServiceType[] = [
  { id: "crane_lift", code: "crane_lift", name: "Crane lift", unit: "lift", defaultPrice: 25, icon: "🏗️" },
  { id: "snow_plowing", code: "snow_plowing", name: "Snow plowing", unit: "h", defaultPrice: 37.5, icon: "❄️" },
  { id: "excavation", code: "excavation", name: "Excavation", unit: "h", defaultPrice: 45, icon: "🪨" },
  { id: "hourly_work", code: "hourly_work", name: "Hourly work", unit: "h", defaultPrice: 30, icon: "⏱️" },
  { id: "cycle_round", code: "cycle_round", name: "Cycle round", unit: "round", defaultPrice: 12, icon: "🔄" },
];

export const findServiceType = (id?: string): ServiceType | undefined =>
  WORK_SERVICE_TYPES.find((s) => s.id === id);

// A billing-directory service carries a `type` (HOUR/PIECE/…) rather than a
// works unit/icon, so map it onto the display shape the service pickers use.
const UNIT_BY_SERVICE_TYPE: Record<Service["type"], string> = {
  HOUR: "h",
  PIECE: "pc",
  LIFT: "lift",
  KM: "km",
  FLAT: "flat",
  QTY: "qty",
  MATRIX: "unit",
};

const ICON_BY_SERVICE_TYPE: Record<Service["type"], string> = {
  HOUR: "⏱️",
  PIECE: "📦",
  LIFT: "🏗️",
  KM: "🛣️",
  FLAT: "🧾",
  QTY: "📦",
  MATRIX: "🧮",
};

/**
 * Map a company Services-directory entry onto the works service-picker option
 * shape. `id` is the service's _id — persisted as the line's serviceTypeId, and
 * `defaultPrice` seeds the driver's price field. MATRIX services have no fixed
 * price (falls back to 0); their zone/band axes and price grid ride along so the
 * picker can make the driver choose a cell and resolve the actual price.
 */
export const directoryServiceToType = (s: Service): ServiceType => ({
  id: s._id,
  code: s.serviceId,
  name: s.name,
  unit: UNIT_BY_SERVICE_TYPE[s.type] ?? "unit",
  defaultPrice: s.price ?? 0,
  // `defaultPrice` has to stay a number — the picker multiplies it — so the
  // fact that there was nothing to put in it travels separately. A MATRIX card
  // DOES state its rates (the grid is the price), so it is never "no rate":
  // an unresolved zone/band is a different complaint, and the picker makes it.
  hasRate: s.type === "MATRIX" || s.price != null,
  icon: ICON_BY_SERVICE_TYPE[s.type] ?? "🧾",
  tax: s.tax,
  pricingType: s.type,
  ...(s.type === "MATRIX"
    ? { zones: s.zones ?? [], bands: s.bands ?? [], priceMatrix: s.priceMatrix ?? [] }
    : {}),
});
